import Map from 'ol/Map.js'
import View from 'ol/View.js'
import BingMaps from 'ol/source/BingMaps.js'
import { VectorLayer } from './layers/RendererLayer.js'
import { Tile as TileLayer } from 'ol/layer.js'
import Feature from 'ol/Feature.js'
import Point from 'ol/geom/Point.js'
import LineString from 'ol/geom/LineString.js'
import * as olExtent from 'ol/extent'
import { Vector as VectorSource } from 'ol/source.js'
import { EditRectangleTool } from './frames/EditRectangleTool.js'
import { AddRectangleInteraction } from './frames/AddRectangleInteraction.js'
import { FrameLayer } from './frames/FrameLayer.js'
import { GeoJSON } from 'ol/format'
import { Modify } from 'ol/interaction'
import { ElementsLayer } from './elements/ElementsLayer.js'
import DrawElement from './elements/DrawElement.js'
import { ElementFeature } from '../utils/ElementFeature.js'
import { getElement } from '@Utils/Elements.js'
import { GoogleMapTile, GoogleMapTileLayer } from './layers/GoogleMapTile.js'
import { Control } from 'ol/control.js'
import { Translate } from 'ol/interaction.js'
import * as Utils from '@Utils/Math.js'
import { simplifyFeature, getIntersectingPolygons, isContainedInPolygon } from '@Utils/FeatureUtils.js'
import { noModifierKeys, altKeyOnly, singleClick, always } from 'ol/events/condition.js'
import UndoRedo from 'ol-ext/interaction/UndoRedo.js'
import { fromLonLat } from 'ol/proj'
import { toLonLat, transformWithProjections } from 'ol/proj.js'
import { deconstructHoleId } from '@Utils/DataController.js'
import { MeasureTool } from './tools/MeasureTool.js'
import { nanoid } from 'nanoid'
import { UnionTool } from './tools/UnionTool.js'
import { AzureMapsTile, AzureMapsTileLayer } from './layers/AzureMaps.js'
import { PlaylineLayer } from './playline/PlaylineLayer.js'
import cspline from 'ol-ext/render/Cspline'
import { fromExtent } from 'ol/geom/Polygon'

let logos = {
	google: "/logotypes/googlemaplogo.png",
	bing: "/logotypes/bingmaplogo.png",
	azuremaps: "/logotypes/azuremapslogo.svg"
}
const ignorePropertyChangeKeys = ['selected', 'focused', 'hidden', 'clearCache', 'intersecting', 'overlapping', 'modifyGeometry', 'filtered']
const propertiesToRemove = ['selected', 'focused', 'hidden', 'filtered', 'unfinished', 'overlapping', 'intersecting', 'modifyGeometry', 'originalGeometry', 'clearCache']
function resetFeatureEditProperties(f) {
	for (let prop of propertiesToRemove) {
		f.unset(prop)
	}
}
function cleanFeatureProperties(f) {
	for (let p of propertiesToRemove) {
		delete f.properties[p]
	}
	return f
}
function updatev1(feature) {
	if (feature.get('elementName') === 'gavel') {
		feature.set('elementName', 'gravel')
	}
	if (feature.get('elementName') === 'greenFocus') {
		feature.set('elementName', 'greenfocus')
	}
	if (feature.get('elementName') === 'sparseforest') {
		feature.set('elementName', 'forest')
		if (!feature.get('forestDensity')) {
			feature.set('forestDensity', 3)
		}
	}
	if (feature.get('elementName') === 'denseforest') {
		feature.set('elementName', 'forest')
		if (!feature.get('forestDensity')) {
			feature.set('forestDensity', 5)
		}
	}
	if (feature.get('elementName') === 'smalltree') {
		feature.set('elementName', 'tree')
	}
	if (feature.get('elementName') === 'bigtree') {
		feature.set('elementName', 'tree')
	}
}
function applyFilters(context, properties) {
	let filter = []
	if (properties.saturate) {
		filter.push(`saturate(${properties.saturate}%)`)
	}
	if (properties.sepia) {
		filter.push(`sepia(${properties.sepia}%)`)
	}
	if (properties.hue) {
		filter.push(`hue-rotate(${properties.hue}deg)`)
	}
	if (properties.brightness) {
		filter.push(`brightness(${properties.brightness}%)`)
	}
	if (properties.contrast) {
		filter.push(`contrast(${properties.contrast}%)`)
	}
	if (properties.grayscale) {
		filter.push(`grayscale(${properties.grayscale}%)`)
	}
	if (properties.invert) {
		filter.push(`invert(${properties.invert}%)`)
	}
	if (properties.blur) {
		filter.push(`blur(${properties.blur}px)`)
	}
	if (filter.length < 1) {
		context.filter = 'none'
		return
	}
	context.filter = filter.join(' ')
	context.globalCompositeOperation = 'source-over'
}

export default class EditorCanvas {
	map
	view
	data
	availableMapServices = [];
	target
	bingMap
	hasUpdates = false;
	translateTool
	editFramesTool
	elementFilters = []
	addFrameTool = new AddRectangleInteraction(import.meta.env.VITE_RENDER_HEIGHT / import.meta.env.VITE_RENDER_WIDTH);
	addRectangleTool = new AddRectangleInteraction();
	drawTool
	modifyTool
	viewPadding = [140, 100, 70, 100];
	selectedFeature
	selectedElement
	mapFilterPropertites = {
		google: {},
		bing: {},
		osm: {
			grayscale: 80,
			invert: 100
		}
	};
	focusedFrameFeature
	focusedFrameLayer
	focusedFrameSource

	unionTool = null
	modifyFrameSource = new VectorSource();
	modifyToolSource = new VectorSource();
	modifyRectangleSource = new VectorSource();
	sharedElementsSource = new VectorSource();
	frameElementSources = [];
	framesSource = new VectorSource();
	courseFramesSource = new VectorSource();
	courseElementSources = [];

	addFramesSource = new VectorSource();
	addFramesLayer
	framesLayer
	sharedElementsLayer
	measureTool
	history
	listeners = {};
	currentTool
	currentMapService = 'google';
	playlines = {}
	constructor(target, options, onRenderComplete) {
		let _this = this
		this.target = target
		this.undoRedo = new UndoRedo()
		this.undoRedo.setMaxLength(50)
		if (!options) {
			options = {}
		}
		this.addRectangleTool.removePropertiesAfterDraw = false
		this.subscription = options.subscription ?? 'basic'
		this.showIntersectingElements = options.showIntersectingElements === true ?? false
		this.showOverlappingElements = options.showOverlappingElements === true ?? false
		document.addEventListener(
			'keydown',
			function (event) {
				if (document.activeElement.tagName === 'INPUT' || document.activeElement.tagName === 'TEXTAREA') {
					return
				}
				if (this.modifyTool) {
					if (event.altKey) {
						this.modifyTool.pixelTolerance_ = 14
					} else {
						this.modifyTool.pixelTolerance_ = 7
					}
				}
				if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() == 'z') {
					if (this.currentTool) {
						return
					}
					if (event.shiftKey) {
						this.undoRedo.redo()
					} else {
						this.undoRedo.undo()
					}
				} else if (event.key == 'Backspace' || event.key == 'Delete') {
					this.deleteSelected()
				} else if (event.key === 'Enter') {
					this.drawTool?.finishDrawing()
				}
			}.bind(this),
			false
		)
		window.addEventListener('licenseChanged', (event) => {
			_this.onLicenseChanged(event.detail.subscription)
		})
		let mapServices = []
		if (options.serviceKeys) {
			if (options.serviceKeys.google) {
				this.googleMap = new GoogleMapTileLayer({
					visible: false,
					maxZoom: 22,
					source: new GoogleMapTile({
						apiKey: options.serviceKeys.google,
						mapType: 'satellite'
					})
				})
				mapServices.push(this.googleMap)
				this.availableMapServices.push('google')
				this.addMapFilters(this.googleMap, 'google')
			}
			if (options.serviceKeys.azureMaps) {
				this.azureMap = new AzureMapsTileLayer({
					visible: false,
					preload: Infinity,
					maxZoom: 23,
					source: new AzureMapsTile({
						key: options.serviceKeys.azureMaps,
						maxZoom: 23
					})
				})
				mapServices.push(this.azureMap)
				this.availableMapServices.push('azuremaps')
				this.addMapFilters(this.azureMap, 'azuremaps')
			}
			if (options.serviceKeys.bing) {
				this.bingMap = new TileLayer({
					visible: false,
					preload: Infinity,
					maxZoom: 20,
					source: new BingMaps({
						key: options.serviceKeys.bing,
						imagerySet: 'Aerial',
						maxZoom: 20
					})
				})
				mapServices.push(this.bingMap)
				this.availableMapServices.push('bing')
				this.addMapFilters(this.bingMap, 'bing')
			}
		}
		if (localStorage) {
			let service = localStorage.getItem('GolfMap_selectedMapService')
			if (this.availableMapServices.includes(service)) {
				this.currentMapService = service
			}
		}
		// this.osmMap = new TileLayer({
		//     source: new OSMSource(),
		//     visible: false
		// });
		// mapServices.push(this.osmMap)
		// this.addMapFilters(this.osmMap, "osm")

		this.view = new View({
			//center: center,
			zoom: 16,
			maxZoom: 21
		})
		this.map = new Map({
			layers: mapServices,
			target: target,
			view: this.view
		})
		this.framesLayer = new FrameLayer(this.map, this.framesSource)
		this.courseFramesLayer = new FrameLayer(this.map, this.courseFramesSource)
		this.addFramesLayer = new FrameLayer(this.map, this.addFramesSource)
		this.playlineLayer = new PlaylineLayer(this.map)
		this.playlineLayer.onPlaylineChanged = function (holeId, data) {
			this.playlines[holeId] = data
			this.trigger('playlineChanged', holeId, data)
		}.bind(this)
		this.translateTool = new Translate({
			condition: function (event) {
				let features = event.map.getFeaturesAtPixel(event.pixel)
				if (features.length < 1) {
					return false
				}
				let f = features[0]
				if (f.get('objectType') === 'playline') {
					return false
				}
				if (f.get('elementName') || f.getGeometry().getType() != 'Point') {
					return true
				}
				return false //platformModifierKeyOnly(event);
			},
			filter: function (feature, layer) {
				if (this.focusedFrameFeature == feature) {
					return this.selectedFeature == feature
				}
				if (this.selectedFeature == feature) {
					return true
				}
				return false
			}.bind(this)
		})

		this.map.on(
			'click',
			function (evt) {

				if (noModifierKeys(evt) == false) {
					return
				}
				if (this.currentTool && this.currentTool !== 'union') {
					return
				}
				if (this.measureTool) {
					return
				}
				if (this.playlineLayer.isActive) {
					return
				}
				let pixel = evt.pixel
				let features = this.map.getFeaturesAtPixel(pixel)
				if (features.length < 1) {
					this.deselectFeature()
					return
				}
				for (let feature of features) {
					if (feature.get('objectType') === 'playline') {
						continue
					}
					if (feature.get('hidden')) {
						continue
					}
					if (this.focusedFrameFeature && feature.get('objectType') == 'frame') {
						this.deselectFeature()
						continue
					}
					if (feature.get('selected')) {
						continue
					}
					if (this.unionTool) {

						this.unionTool.select(feature)
						break
					}
					this.selectFeature(feature)
					break
				}
			}.bind(this)
		)
		this.addFrameTool.onFeatureComplete = function (p, f) {
			this.deactivateTool()
			setTimeout(() => {
				this.selectFeature(f)
			}, 120)
		}.bind(this)
		this.map.on('loadend', function () {
			if (onRenderComplete) {
				onRenderComplete()
			}
		})

		if (options.extent) {
			this.fit(options.extent)
		} else {
			this.fitAllFrames()
		}
		if (options.data) {
			this.loadJson(options.data)
		}
		this.map.addInteraction(this.translateTool)
		this.framesLayer.set('name', 'Frames')
		this.courseFramesSource.set('name', 'CourseFrames')
		this.sharedElementsLayer = new ElementsLayer(this, this.sharedElementsSource, false)
		this.sharedElementsLayer.set('name', 'SharedElements')
		this.sharedElementsLayer.setZIndex(4)
		this.map.addLayer(this.sharedElementsLayer)
		this.modifyTool = new Modify({
			source: this.modifyToolSource,
			pixelTolerance: 7,
			condition: function (event) {
				let features = event.map.getFeaturesAtPixel(event.pixel)
				if (features.length < 1) {
					return false
				}
				let f = features[0]
				let objectType = f.get('objectType')
				if (objectType === 'playline') {
					return false
				}
				return true
			},
			deleteCondition: function (event) {
				if (_this.selectedElement && _this.selectedElement.hasMaxPointConstraints()) {
					return false
				}
				return altKeyOnly(event) && singleClick(event)
			},
			insertVertexCondition: function (event) {
				if (_this.selectedElement && _this.selectedElement.hasMaxPointConstraints()) {
					return false
				}
				return always(event)
			}
		})
		this.modifyTool.on('modifyend', function (evt) {
			let f = evt.features.getArray()[0]
			//if (f.get('elementName')) {
			_this.trigger('featureUpdated', f, evt)
			//}
		})
		this.translateTool.on('translateend', function (evt) {
			let f = evt.features.getArray()[0]
			if (evt.coordinate[0] !== evt.startCoordinate[0] || evt.coordinate[1] !== evt.startCoordinate[1]) {
				_this.trigger('featureUpdated', f, evt)
				_this.updateFeatureHighlights()
			}
		})
		this.map.addInteraction(this.modifyTool)
		this.map.addLayer(this.framesLayer)
		this.map.addLayer(this.courseFramesLayer)
		if (['premium'].includes(this.subscription)) {
			this.map.addLayer(this.playlineLayer)
		}
		this.framesLayer.setZIndex(1)
		this.editFramesTool = new EditRectangleTool(this.map, this.modifyFrameSource, this.vector)
		this.editRectangleTool = new EditRectangleTool(this.map, this.modifyRectangleSource, this.vector)

		// this.editRectangleTool.modifyend = function (feature) {	
		// 	_this.trigger('featureUpdated', feature, null)
		// };
		this.addSourceListerners(this.sharedElementsSource)
		this.addSourceListerners(this.framesSource)
		this.addSourceListerners(this.courseFramesSource)
		this.setMapService(this.currentMapService, true)

		this.map.addInteraction(this.undoRedo)

	}
	onLicenseChanged(subscription) {
		this.subscription = subscription
		if (['premium'].includes(this.subscription)) {
			this.map.addLayer(this.playlineLayer)
		} else {
			this.map.removeLayer(this.playlineLayer)
		}
	}
	escape() {
		if (this.unionTool) {
			this.unionTool.cancel()
			this.unionTool = null
			this.deactivateTool()
		} else if (this.measureTool) {
			this.deactivateTool()
		} else if (this.drawTool) {
			if (this.drawTool.getPointerCount() > 0) {
				this.drawTool.abortDrawing()
				if (this.drawTool._modifyFeature) {
					this.drawTool._modifyFeatureSource.addFeature(this.drawTool._modifyFeature)
				}
			} else {
				if (this.drawTool._modifyFeature) {
					this.drawTool._modifyFeatureSource.addFeature(this.drawTool._modifyFeature)
				}
				this.deactivateTool()
			}
		} else if (this.addFrameTool.feature_) {
			this.addFrameTool.cancel()
			this.deactivateTool()
		} else if (this.addRectangleTool.feature_) {
			this.addRectangleTool.cancel()
			this.deactivateTool()
		} else if (this.currentTool) {
			this.deactivateTool()
		} else {
			this.deselectFeature()
		}
	}
	on(event, cb) {
		if (!this.listeners[event]) {
			this.listeners[event] = []
		}
		this.listeners[event].push(cb)
	}
	setMapFilters(properties, service) {
		if (service == 'google' || service == 'osm' || service == 'bing') {
			this.mapFilterPropertites[service] = properties
		}
	}
	addMapFilters(tileLayer, service) {
		let _this = this
		tileLayer.on('prerender', (evt) => {
			if (evt.context && _this.mapFilterPropertites[service]) {
				const context = evt.context
				applyFilters(context, _this.mapFilterPropertites[service])
			}
		})
		tileLayer.on('postrender', (evt) => {
			if (evt.context) {
				const context = evt.context
				context.filter = 'none'
			}
		})
	}
	addMapLogo(service) {
		if (this.mapLogoControl) {
			this.map.removeControl(this.mapLogoControl)
		}
		let url = logos[service]
		if (url) {
			let c = document.createElement('img')
			c.src = url
			c.style.position = 'absolute'
			c.style.bottom = '10px'
			c.style.left = '10px'
			c.style.height = '24px'
			const mapLogoControl = new Control({ element: c })
			this.map.addControl(mapLogoControl)
			this.mapLogoControl = mapLogoControl
		}
	}
	setMapService(service, force) {
		if (!this.availableMapServices.includes(service)) {
			return
		}
		if (!force && this.currentMapService == service) {
			return
		}
		localStorage.setItem('GolfMap_selectedMapService', service)
		this.currentMapService = service
		let currentServiceLayer
		if (this.googleMap) {
			this.googleMap.setVisible(service == 'google')
			if (service === 'google') {
				this.view.setMaxZoom(21)
			}
		}
		if (this.azureMap) {
			this.azureMap.setVisible(service == 'azuremaps')
			if (service === 'azuremaps') {
				this.view.setMaxZoom(22)
			}
		}
		if (this.bingMap) {
			this.bingMap.setVisible(service == 'bing')
			if (service === 'bing') {
				this.view.setMaxZoom(19)
			}
		}
		if (this.osmMap) {
			this.osmMap.setVisible(service == 'osm')
		}
		this.trigger('mapServiceChanged', service)
		this.addMapLogo(this.currentMapService)
	}
	convertFeaturesToGeoJSON(feature) {
		let writer = new GeoJSON()
		return writer.writeFeatures(feature)
	}
	convertFeatureToGeoJSON(feature) {
		let writer = new GeoJSON()
		return writer.writeFeature(feature)
	}
	filterElements(filters) {
		this.elementFilters = filters
		for (let source of [this.sharedElementsSource, ...this.frameElementSources, ...this.courseElementSources, this.framesSource, this.courseFramesSource]) {
			if (!source) {
				continue
			}
			let features = source.getFeatures()
			for (let feature of features) {
				feature.set('filtered', false)
				for (let filter of filters) {
					if (filter.values) {
						if (filter.values.includes(feature.get(filter.key))) {
							if (filter.properties) {
								for (let prop of filter.properties) {
									if (feature.get(prop)) {
										feature.set('filtered', true)
										break
									}
								}
							} else {
								feature.set('filtered', true)
								break
							}
						}
					} else if (feature.get(filter.key) == filter.value) {
						feature.set('filtered', true)
						break
					}
				}
			}
		}
		if (filters.find((f) => f.key === 'objectType' && f.value === 'playline')) {
			this.playlineLayer.setVisible(false)
		} else {
			this.playlineLayer.setVisible(true)
		}
	}
	getCourseRenderData(courseId, controller) {
		let frame = this.getCourseFrameFeature(courseId)
		if (!frame) {
			console.error(`Cannot find frame with id ${courseId}`)
			return null
		}
		let writer = new GeoJSON()
		let obj = {}
		obj.id = courseId
		obj.frame = writer.writeFeature(frame)

		let surroundings = []
		surroundings = this.sharedElementsSource.getFeatures()
		let elements = this.courseElementSources.find((s) => s.get('courseId') == courseId)
		if (elements) {
			surroundings = surroundings.concat(elements.getFeatures())
		}
		for (let source of this.frameElementSources) {
			let els = source.getFeatures().filter((f) =>
				f.get('elementName') !== "focus"
				&& f.get('elementName') !== "greenfocus"
				&& f.get('elementName') !== "iconcollection"
				&& f.get('elementName') !== "holeicon"
				//&& !(f.get('elementName').startsWith('tee') && f.get('elementName').length > 3)
			)
			surroundings = surroundings.concat(els)
		}

		for (let frame of this.framesSource.getFeatures()) {
			let extent = frame.getGeometry().getExtent()
			let center = olExtent.getCenter(extent)
			let point = new Feature({
				geometry: new Point(center)
			})
			point.set('elementName', 'text')
			let holeId = frame.get('holeId')
			if (!holeId) {
				console.error('Frame does not have holeId', frame)
				continue
			}
			let props = this.getPlaylineProperties(holeId)
			if (props && props.assembledCoordinates) {
				let holeInfo = deconstructHoleId(holeId)
				let coords = cspline(props.assembledCoordinates, { tension: 0.5, pointsPerSeg: 50, normalize: true })
				let middleCoordinate = coords[Math.floor(coords.length / 2)]
				let point = new Feature({
					geometry: new Point(middleCoordinate)
				})
				point.set('elementName', 'text')
				point.set('backgroundShape', 'circle')
				point.set('text', holeInfo.holeNumber)
				point.set('textUsedForMeasuring', "00")
				if (controller) {
					point.set('backgroundShapeColor', controller.getGroupSetting(holeInfo.holeGroupId, 'holecolor') ?? '#ffffff')
					point.set('textColor', controller.getGroupSetting(holeInfo.holeGroupId, 'holetextcolor') ?? '#000000')
				} else {
					point.set('backgroundShapeColor', '#ffffff')
					point.set('textColor', '#000000')
				}
				point.set('uid', frame.get('uid') + '-text')
				let playline = new Feature({
					geometry: new LineString(coords)
				})
				playline.set('greenCoordinate', props.greenPointCoordiantes)
				playline.set('teeCoordinate', props.teePointCoordinates)
				playline.set('objectType', 'playline')
				playline.set('holeId', holeId)
				playline.set('rounded', true)
				playline.set('uid', frame.get('uid') + '-playline')
				surroundings.push(playline)
				surroundings.push(point)
			}
		}
		let focus = surroundings.find((f) => f.get('elementName') === 'focus')
		if (focus) {
			obj.focusFeatureId = focus.get('uid')
		}
		obj.elements = writer.writeFeatures(surroundings)
		obj.extent = frame.getGeometry().getExtent()
		//console.log(JSON.stringify(obj))
		return obj
	}
	getGreenRenderData(holeId) {
		let frame = this.getHoleFrameFeature(holeId)
		if (!frame) {
			console.error(`Cannot find frame with id ${holeId}`)
			return null
		}
		let elementSource = this.frameElementSources.find((s) => s.get('holeId') == holeId)
		if (!elementSource) {
			console.error(`Cannot find frame elements with id ${holeId}`)
			return null
		}
		let els = elementSource.getFeatures().filter((f) => f.get('elementName') !== "focus")
		let greenfocus = els.find((f) => f.get('elementName') === 'greenfocus')
		if (!greenfocus) {
			console.info(`Cannot find greenfocus for hole with id ${holeId}, skipping`)
			return null
		}
		let writer = new GeoJSON()
		let obj = {}
		obj.id = holeId
		let surroundings = []
		let extent = greenfocus.getGeometry().getExtent()
		let greenFrame = fromExtent(extent)
		surroundings = this.sharedElementsSource.getFeatures()
		surroundings = surroundings.concat(els)
		obj.frame = writer.writeFeature(new Feature(greenFrame))
		obj.focusFeatureId = greenfocus.get('uid')
		obj.rotationFeature = writer.writeFeature(frame)
		obj.elements = writer.writeFeatures(surroundings)
		obj.extent = extent
		return obj
	}
	getHoleRenderData(holeId) {
		let frame = this.getHoleFrameFeature(holeId)
		if (!frame) {
			console.error(`Cannot find frame with id ${holeId}`)
			return null
		}
		let writer = new GeoJSON()
		let obj = {}
		obj.id = holeId
		let surroundings = []
		surroundings = this.sharedElementsSource.getFeatures()
		let elements = this.frameElementSources.find((s) => s.get('holeId') == holeId)

		if (elements) {
			let els = elements.getFeatures().filter((f) => f.get('elementName') !== "greenfocus")
			surroundings = surroundings.concat(els)
		}
		let focus = surroundings.find((f) => f.get('elementName') === 'focus')
		obj.frame = writer.writeFeature(frame)
		obj.elements = writer.writeFeatures(surroundings)
		if (focus) {
			obj.focusFeatureId = focus.get('uid')
		}
		obj.extent = frame.getGeometry().getExtent()
		return obj
	}
	deleteSelected(force) {
		if (!this.selectedFeature) {
			return
		}
		if (this.deleteFeature(this.selectedFeature, force)) {
			this.deselectFeature(this.selectedFeature)
		}
	}
	deleteFeature(feature, force) {
		let source = this.getFeatureSource(feature)
		let holeId = feature.get('holeId')
		let courseId = feature.get('courseId')

		if (feature.get('objectType') == 'frame') {
			if (!force) {
				let e = new Event('warning')
				e.message = "Cannot delete frame using backspace. Please use the button provided in the element options."
				window.dispatchEvent(e)
				console.warn('Cannot delete frame using backspace, use force = true to delete frame')
				return false
			}
			if (feature.get('focused')) {
				this.unfocus()
				this.fitAllFrames()
			}
			if (holeId) {
				this.frameElementSources = this.frameElementSources.filter((s) => s.get('holeId') != holeId)
			} else if (courseId) {
				this.courseElementSources = this.courseElementSources.filter((s) => s.get('courseId') != courseId)
			}
		}
		// let uid = feature.get('uid')
		// for (let s of [this.sharedElementsSource, ...this.frameElementSources, ...this.courseElementSources, this.framesSource, this.courseFramesSource]) {
		// 	let f = s.getFeatures().filter((f) => f.get('uid') === uid)
		// 	if(f[0]) {
		// 		s.removeFeature(f[0])
		// 	}
		// }
		if (source) {
			source.removeFeature(feature)
		}
		this.modifyToolSource.clear()
		this.modifyRectangleSource.clear()
		this.modifyFrameSource.clear()
		return true
	}
	off(event, cb) {
		if (!this.listeners[event]) {
			return
		}
		this.listeners[event] = this.listeners[event].filter((c) => c != cb)
	}
	fit(extent) {
		this.view.setRotation(0)
		this.view.fit(extent, {
			padding: this.viewPadding
		})
	}
	rotate(angle, animated) {
		if (animated == undefined) {
			animated = true
		}
		if (!angle) {
			angle = Utils.degreesToRadians(0)
		}
		if (animated) {
			this.view.animate({
				duration: 500,
				rotation: angle
			})
		} else {
			this.view.setRotation(Utils.degreesToRadians(angle))
		}
	}
	panToFeature(feature) {
		this.view.fit(feature.getGeometry(), {
			padding: this.viewPadding
		})
	}
	fitFeature(feature, animated, rotate) {
		if (animated == undefined) {
			animated = true
		}
		let angle
		let geometry = feature.getGeometry()
		let coordinates = geometry.getCoordinates()[0]
		if (rotate && coordinates.length > 1) {
			angle = Math.atan2(coordinates[1][1] - coordinates[0][1], coordinates[1][0] - coordinates[0][0]) + Utils.degreesToRadians(-90)
		}
		let map = this.map
		let view = this.view
		if (animated) {
			let extent = geometry.getExtent()
			let center = olExtent.getCenter(extent)
			let finalZoom = map.getView().getZoomForResolution(map.getView().getResolutionForExtent(extent, map.getSize()))
			if (finalZoom < 21) {
				finalZoom = 21
			}
			view.animate({
				center: center,
				duration: 500,
				zoom: finalZoom,
				rotation: angle,
				padding: this.viewPadding
			})
		} else {
			if (angle) {
				view.setRotation(angle)
			}
			view.fit(geometry, {
				padding: this.viewPadding
			})
		}
	}
	fitSource(source, animated) {
		if (animated === undefined) {
			animated = false
		}
		if (source.getFeatures().length < 1) {
			return
		}
		let extent = source.getExtent()
		if (!extent) {
			return
		}
		if (animated) {
			let center = olExtent.getCenter(extent)
			let finalZoom = this.map.getView().getZoomForResolution(this.map.getView().getResolutionForExtent(extent, this.map.getSize()))
			this.view.animate({
				center: center,
				duration: 500,
				zoom: finalZoom,
				rotation: 0,
				padding: this.viewPadding
			})
		} else {
			this.view.setRotation(0)
			this.view.fit(extent, {
				padding: this.viewPadding
			})
		}
	}
	fitAllFrames(animated) {
		if (this.framesSource.getFeatures().length < 1) {
			this.panTo([15.908203125000002, 62.79493487887009], 5, animated)
		} else {
			this.fitSource(this.framesSource, animated)
		}
	}
	getExtent() {
		return this.view.calculateExtent(this.map.getSize())
	}
	selectFeature(feature, force) {
		this.deselectFeature()
		if (this.currentTool) {
			this.deactivateTool()
		}
		feature.set('clearCache', true)
		this.modifyToolSource.clear()
		this.modifyRectangleSource.clear()
		this.modifyFrameSource.clear()
		if (feature == null) {
			return
		}
		if (this.focusedFrameFeature && force !== true) {
			if (this.focusedFrameFeature == feature) {
				return
			}
		}
		if (feature.get('elementName') && !feature.get('hidden')) {
			let element = getElement(feature.get('elementName'))
			if (element.tooltype === 'Rectangle') {
				this.modifyRectangleSource.addFeature(feature)
			} else {
				this.modifyToolSource.addFeatures([feature])
			}
		}

		this.selectedFeature = feature
		this.selectedFeature.set('selected', true)
		if (feature.get('elementName')) {
			this.selectedElement = new ElementFeature(feature)
			this.trigger('elementSelected', this.selectedElement)
		} else if (feature.get('objectType') === 'frame') {
			this.trigger('frameSelected', feature)
			this.modifyFrameSource.addFeature(feature)
		}
		this.addFeatureListerners(this.selectedFeature)
		this.updateFeatureHighlights()
	}
	deselectFeature() {
		this.modifyToolSource.clear()
		this.modifyRectangleSource.clear()
		let s = this.selectedFeature
		let e = this.selectedElement
		if (s) {

			this.removeFeatureListerners(s)
			this.selectedFeature.unset('selected')
			this.selectedFeature = null
			this.selectedElement = null
			this.trigger('elementSelected', null, e)
			if (s.get('objectType') == 'frame') {
				if (!this.focusedFrameFeature) {
					this.trigger('frameSelected', null)
				}
			}
		}
		this.updateFeatureHighlights()
	}
	getAvilableEvents() {
		return ['elementSelected', 'frameSelected', 'frameFocused', 'sourceUpdated', 'featureUpdated', 'featureRemoved', 'featureAdded', 'drawEnd', 'elementOrderChanged', 'currentToolChange', 'measurementUpdated']
	}
	getFrameFeature(holeId) {
		let source = this.framesSource
		let features = source.getFeatures()
		for (let f of features) {
			if (f.get('holeId') == holeId) {
				return f
			}
		}
		return null
	}
	setIconScale(scale) {
		if (this.focusedFrameFeature) {
			this.focusedFrameFeature.set('iconScale', scale)
			this.trigger('featureUpdated', this.focusedFrameFeature, { type: 'propertychange' })
			this.refresh()
		}
	}
	getFrame(holeId) {
		let source = this.framesSource
		let features = source.getFeatures()
		for (let f of features) {
			if (f.get('holeId') == holeId) {
				let writer = new GeoJSON()
				return writer.writeFeatureObject(f)
			}
		}
		return null
	}
	getPlayline(holeId) {
		if (this.playlines[holeId]) {
			return JSON.stringify(this.playlines[holeId])
		}
		return null
	}
	resetPlaylineOptions(holeId) {
		this.playlines[holeId] = {
			greenPoint: 'greencenter',
			coordinates: []
		}
		this.#linkPlaylineLayer()
		this.#activatePlaylineTool()
	}
	setPlaylineOption(holeId, name, value) {
		if (!this.playlines[holeId]) {
			this.playlines[holeId] = {
				greenPoint: 'greencenter',
				coordinates: []
			}
		}
		this.playlines[holeId][name] = value
		this.#linkPlaylineLayer()
		this.#activatePlaylineTool()
		this.trigger('playlineChanged', holeId, this.playlines[holeId])
	}
	hasSurroundings() {
		return this.sharedElementsSource.getFeatures().length > 0
	}
	getAnalyticsForHole(holeId) {
		let hasOutline = this.getHoleFrameFeature(holeId)
		let source = this.frameElementSources.find((s) => s.get('holeId') == holeId)
		if (!source) {
			return {
				distance: {
					m50: null,
					m100: null,
					m150: null,
					m200: null,
				},
				hasGreen: false,
				hasTee: false,
				hasFocus: false,
				hasGreenFocus: false,
				hasOutline: hasOutline ? true : false,
				green: {
					front: null,
					back: null,
					center: null
				},
				tee: {
					yellowblack: false,
					greenwhite: false,
					orange: false,
					red: false,
					blue: false,
					yellow: false,
					white: false,
					black: false
				}
			}
		}
		function hasFeature(features, elementName) {
			return features.find((f) => f.get('elementName') === elementName) ? true : false
		}
		let features = source.getFeatures()
		let data = {
			hasOutline: true,
			hasGreen: hasFeature(features, 'green'),
			hasTee: hasFeature(features, 'tee'),
			hasFocus: hasFeature(features, 'focus'),
			hasGreenFocus: hasFeature(features, 'greenfocus'),
			green: {
				front: hasFeature(features, 'greenfront'),
				back: hasFeature(features, 'greenback'),
				center: hasFeature(features, 'greencenter')
			},
			tee: {
				yellowblack: hasFeature(features, 'teeyellowblack'),
				greenwhite: hasFeature(features, 'teegreenwhite'),
				orange: hasFeature(features, 'teeorange'),
				red: hasFeature(features, 'teered'),
				blue: hasFeature(features, 'teeblue'),
				yellow: hasFeature(features, 'teeyellow'),
				white: hasFeature(features, 'teewhite'),
				black: hasFeature(features, 'teeblack')
			}
		}
		return data
	}
	getMiscDataForHole(holeId) {
		let source = this.frameElementSources.find((s) => s.get('holeId') == holeId)
		if (!source) {
			return null
		}
		function getCoordinatesForFeature(features, elementName) {
			let feature = features.find((f) => f.get('elementName') === elementName)
			if (!feature) {
				return null
			}
			let coords = toLonLat(feature.getGeometry().getCoordinates())
			return {
				name: 'WGS84',
				epsg: '4326',
				x: 0,
				y: 1,
				coordinates: coords
			}
		}
		let features = source.getFeatures()
		let data = {
			hasGreen: features.find((f) => f.get('elementName') === 'green') ? true : false,
			hasTee: features.find((f) => f.get('elementName') === 'tee') ? true : false,
			green: {
				front: getCoordinatesForFeature(features, 'greenfront'),
				back: getCoordinatesForFeature(features, 'greenback'),
				center: getCoordinatesForFeature(features, 'greencenter')
			},
			tee: {
				yellowblack: getCoordinatesForFeature(features, 'teeyellowblack'),
				greenwhite: getCoordinatesForFeature(features, 'teegreenwhite'),
				orange: getCoordinatesForFeature(features, 'teeorange'),
				red: getCoordinatesForFeature(features, 'teered'),
				blue: getCoordinatesForFeature(features, 'teeblue'),
				yellow: getCoordinatesForFeature(features, 'teeyellow'),
				white: getCoordinatesForFeature(features, 'teewhite'),
				black: getCoordinatesForFeature(features, 'teeblack')
			}
		}
		return data
	}
	getFrameSurroundings(holeId) {
		let source = this.frameElementSources.find((s) => s.get('holeId') == holeId)
		if (!source) {
			return null
		}
		source.getFeatures()
		let writer = new GeoJSON()
		let features = writer.writeFeaturesObject(source.getFeatures())
		for (let f of features.features) {
			cleanFeatureProperties(f)
		}
		return features
	}
	getCourseFrames() {
		let data = []
		let writer = new GeoJSON()
		this.courseFramesSource.getFeatures().forEach((f) => {
			let elements = this.courseElementSources.find((s) => s.get('courseId') == f.get('courseId')) ?? null
			let surroundings = null
			if (elements) {
				surroundings = writer.writeFeaturesObject(elements.getFeatures())
				for (let s of surroundings.features) {
					cleanFeatureProperties(s)
				}
			}
			data.push({
				courseId: f.get('courseId'),
				displayName: f.get('displayName'),
				geometry: cleanFeatureProperties(writer.writeFeatureObject(f)),
				surroundings: surroundings,
				loops: f.get('loops'),
				imageDate: f.get('imageDate')
			})
		})
		return data
	}
	getSurroundings() {
		let writer = new GeoJSON()
		let object = writer.writeFeaturesObject(this.sharedElementsSource.getFeatures())
		for (let f of object.features) {
			cleanFeatureProperties(f)
		}
		return object
	}
	// mergeAllIntersectingElements() {
	// 	let features = this.sharedElementsSource.getFeatures().filter((f) => f.getGeometry().getType() == 'Polygon');
	// 	let newFeatures = [];

	// 	function mergeIntersecting(feature, features) {
	// 		let intersecting = getIntersectingPolygons(feature, features, {
	// 			checkOrder: true,
	// 			ignoreFocus: true,
	// 			matchElementName: true,
	// 			ignoreForests: false
	// 		});
	// 		if (intersecting.length < 1) {
	// 			return false
	// 		}
	// 		let i = intersecting[0];
	// 		let unionTool = new UnionTool(function (feature) {
	// 			newFeatures.push(feature);
	// 		})
	// 		unionTool.select(feature);
	// 		if(unionTool.select(i)) {
	// 			return true
	// 		}
	// 		return true
	// 	}

	// }
	removeAllOverlappingElements() {
		let features = this.sharedElementsSource.getFeatures().filter((f) => f.getGeometry().getType() == 'Polygon')
		for (let f of features) {
			if (f.get('overlapping')) {
				this.sharedElementsSource.removeFeature(f)
			}
		}
	}
	resetElementsToDefaultOptions() {
		this.deselectFeature()
		let elementFeature = new ElementFeature()
		let features = this.sharedElementsSource.getFeatures()
		for (let f of features) {
			elementFeature.feature = f
			elementFeature.resetToDefaults(null, true)
		}
		for (let source of this.frameElementSources) {
			let features = source.getFeatures()
			for (let f of features) {
				elementFeature.feature = f
				elementFeature.resetToDefaults(null, true)
			}
		}
		this.sharedElementsLayer?.clearCache()
		this.focusedFrameLayer?.clearCache()
	}
	loadJson(data, fitFrames, clearHistory, resetToDefaults) {
		this.isRealoadingData = true
		this.playlines = {}
		let reader = new GeoJSON()
		let elementFeature = new ElementFeature()
		if (fitFrames == undefined) {
			fitFrames = true
		}
		if (resetToDefaults === undefined) {
			resetToDefaults = false
		}
		if (clearHistory === undefined) {
			clearHistory = true
		}
		if (data.playlines) {
			this.playlines = data.playlines
		}
		if (data.frames) {
			this.importFrames({ data: data.frames, reader, resetToDefaults, elementFeature })
		}
		if (data.courseFrames) {
			this.importCourseFrames({ data: data.courseFrames, reader, resetToDefaults, elementFeature })
		}
		if (data.surroundings && data.surroundings != '') {
			this.importSurroundings({ data: data.surroundings, reader, resetToDefaults, elementFeature })
		}
		if (fitFrames) {
			if (this.framesSource.getFeatures().length > 0) {
				this.fitSource(this.framesSource)
			} else {
				this.fitSource(this.sharedElementsSource)
			}
		}
		if (clearHistory) {
			this.undoRedo.clear()
		}
		this.isRealoadingData = false
		this.updateFeatureHighlights()
		if (this.elementFilters) {
			this.filterElements(this.elementFilters)
		}
	}
	importFrames(preferences) {
		const {
			data,
			reader = new GeoJSON(),
			resetToDefaults = false,
			elementFeature = new ElementFeature()
		} = preferences
		this.framesSource.clear()
		this.frameElementSources.forEach((s) => {
			s.clear()
		})
		this.frameElementSources = []
		for (let frame of data) {
			let features_ = reader.readFeatures(frame.geometry)
			features_.forEach((f) => {
				resetFeatureEditProperties(f)
				updatev1(f)
				if (resetToDefaults) {
					elementFeature.feature = f
					elementFeature.resetToDefaults()
				}
				if (f.get('objectType') == 'frame') {
					f.set('holeId', frame.holeId)
					f.set('displayName', frame.displayName)
				}
				if (!f.get('uid')) {
					f.set('uid', nanoid())
				}
			})
			this.framesSource.addFeatures(features_)
			if (frame.surroundings) {
				let features_ = reader.readFeatures(frame.surroundings)
				features_.forEach((f) => {
					resetFeatureEditProperties(f)
					updatev1(f)
					if (resetToDefaults) {
						elementFeature.feature = f
						elementFeature.resetToDefaults()
					}
					let element = getElement(f.get('elementName'))
					let placeover = getElement(f.get('placeabove'))
					let index = element.zIndex
					if (placeover) {
						index = placeover.zIndex + 1
					}
					f.set('order', index)
					if (!f.get('uid')) {
						f.set('uid', nanoid())
					}
					f.set('holeId', frame.holeId)
					if (!f.get('uid')) {
						f.set('uid', nanoid())
					}
				})
				let s = new VectorSource()
				s.set('name', 'FrameElements')
				s.set('holeId', frame.holeId)
				s.addFeatures(features_)
				this.frameElementSources.push(s)
			}
		}
	}

	importCourseFrames(preferences) {
		const {
			data,
			reader = new GeoJSON(),
			resetToDefaults = false,
			elementFeature = new ElementFeature()
		} = preferences
		this.courseFramesSource.clear()
		this.courseElementSources.forEach((s) => {
			s.clear()
		})
		this.courseElementSources = []
		for (let frame of data) {
			let features_ = reader.readFeatures(frame.geometry)
			features_.forEach((f) => {
				resetFeatureEditProperties(f)
				updatev1(f)
				if (resetToDefaults) {
					elementFeature.feature = f
					elementFeature.resetToDefaults()
				}
				if (f.get('objectType') == 'frame') {
					f.set('courseId', frame.courseId)
					f.set('displayName', frame.displayName)
				}
				if (!f.get('uid')) {
					f.set('uid', nanoid())
				}
			})
			this.courseFramesSource.addFeatures(features_)
			if (frame.surroundings) {
				let features_ = reader.readFeatures(frame.surroundings)
				features_.forEach((f) => {
					resetFeatureEditProperties(f)
					updatev1(f)
					if (resetToDefaults) {
						elementFeature.feature = f
						elementFeature.resetToDefaults()
					}
					f.set('courseId', frame.courseId)
					if (!f.get('uid')) {
						f.set('uid', nanoid())
					}
				})
				let s = new VectorSource()
				s.set('name', 'CourseElements')
				s.set('courseId', frame.courseId)
				s.addFeatures(features_)
				this.courseElementSources.push(s)
			}
		}
	}

	importSurroundings(preferences) {
		const {
			data,
			reader = new GeoJSON(),
			resetToDefaults = false,
			elementFeature = new ElementFeature()
		} = preferences
		this.sharedElementsSource.clear()
		let features_ = reader.readFeatures(data)
		features_.forEach((f) => {
			resetFeatureEditProperties(f)
			updatev1(f)
			if (resetToDefaults) {
				elementFeature.feature = f
				elementFeature.resetToDefaults()
			}
			let element = getElement(f.get('elementName'))
			if (element) {
				let placeover = getElement(f.get('placeabove'))
				let index = element.zIndex
				if (placeover) {
					index = placeover.zIndex + 1
				}
				f.set('order', index)
				if (!f.get('uid')) {
					f.set('uid', nanoid())

				}
			} else {
				f.unset('objectType')
				f.unset('frameId')
				f.set('elementName', 'slantedroofbuilding')
				f.set('order', 500)
				if (!f.get('uid')) {
					f.set('uid', nanoid())

				}
			}
		})
		this.sharedElementsSource.addFeatures(features_)
	}

	simplifyAllPolygons(maxPoints = 100) {
		let features = this.sharedElementsSource.getFeatures().filter((f) => f.getGeometry().getType() == 'Polygon')
		for (let f of features) {
			let l1 = f.getGeometry().getCoordinates()[0].length
			if (l1 > maxPoints) {
				simplifyFeature(f)
				let l2 = f.getGeometry().getCoordinates()[0].length
				let sum = l1 - l2
				if (sum) {
					console.log(`simplified polygon by removing ${sum} coordinates`)
				}
			}
		}
		for (let s of this.frameElementSources) {
			let features = s.getFeatures().filter((f) => f.getGeometry().getType() == 'Polygon')
			for (let f of features) {
				let l1 = f.getGeometry().getCoordinates()[0].length
				if (l1 > maxPoints) {
					let l2 = f.getGeometry().getCoordinates()[0].length
					simplifyFeature(f)
					let sum = l1 - l2
					if (sum) {
						console.log(`simplified polygon by removing ${sum} coordinates`)
					}
				}
			}
		}
	}
	toggleFocus() {
		if (this.focusedFrameFeature) {
			this.unfocus()
		} else if (this.selectedFeature) {
			this.focus()
		}
	}
	getCourseFrameFeatures() {
		return this.courseFramesSource.getFeatures()
	}
	getCourseFrameFeature(courseId) {
		for (let f of this.courseFramesSource.getFeatures()) {
			if (f.get('courseId') == courseId) {
				return f
			}
		}
		return null
	}
	getHoleFrameFeature(holeId) {
		for (let f of this.framesSource.getFeatures()) {
			if (f.get('holeId') == holeId) {
				return f
			}
		}
		return null
	}
	updateIntersectingFeatures(feature) {
		let features = this.sharedElementsSource.getFeatures().filter((f) => f.getGeometry().getType() == 'Polygon' && f.get('elementName').includes('building') === false)
		for (let f of features) {
			f.unset('intersecting')
		}
		if (!feature) {
			return
		}
		let intersecting = getIntersectingPolygons(feature, features, {
			checkOrder: true,
			ignoreFocus: true,
			matchElementName: true,
			ignoreForests: false
		})
		for (let f of intersecting) {
			f.set('intersecting', true)
		}
	}
	enableShowIntersectingElements() {
		this.showIntersectingElements = true
		this.updateIntersectingFeatures(this.selectedFeature)
	}
	disableShowIntersectingElements() {
		this.showIntersectingElements = false
		let features = this.sharedElementsSource.getFeatures().filter((f) => f.getGeometry().getType() == 'Polygon')
		for (let f of features) {
			f.unset('intersecting')
		}
	}
	enableShowOverlappingElements() {
		this.showOverlappingElements = true
		this.updateFeatureHighlights()
	}
	disableShowOverlappingElements() {
		this.showOverlappingElements = false
		let features = this.sharedElementsSource.getFeatures().filter((f) => f.getGeometry().getType() == 'Polygon')
		for (let f of features) {
			f.unset('overlapping')
		}
	}
	updateOverlappingElements() {
		let features = this.sharedElementsSource.getFeatures().filter((f) => f.getGeometry().getType() === 'Polygon' && f.get('elementName').includes('building') === false)
		for (let f of features) {
			f.unset('overlapping')
		}
		for (let f of features) {

			let extent = f.getGeometry().getExtent()
			let intersecting = this.sharedElementsSource.getFeaturesInExtent(extent).filter((f) => f.getGeometry().getType() == 'Polygon')

			for (let i of intersecting) {
				if (f.get('order') >= i.get('order')) {
					if (isContainedInPolygon(f, i)) {
						i.set('overlapping', true)
					}
				}
			}
		}
	}
	updateFeatureHighlights() {
		if (this.isRealoadingData) {
			return
		}
		if (this.showIntersectingElements === true) {
			this.updateIntersectingFeatures(this.selectedFeature)
		}
		if (this.showOverlappingElements === true) {
			this.updateOverlappingElements()
		}
	}
	refresh() {
		this.sharedElementsSource.changed()
		this.focusedFrameSource?.changed()
		for (let s of this.frameElementSources) {
			s.changed()
		}
	}
	panTo(coordinates, zoom, animated) {
		if (!zoom) {
			zoom = 14
		}
		if (animated == undefined) {
			animated = true
		}
		let converted = fromLonLat([coordinates[0], coordinates[1]])
		if (animated) {
			this.view.animate({
				center: converted,
				duration: 500,
				zoom: zoom,
				rotation: 0,
				padding: this.viewPadding
			})
		} else {
			this.view.setCenter(converted)
			this.view.setZoom(zoom)
		}
	}
	selectFrame(holeId, panTo, animated, rotate, focus) {
		if (panTo === undefined) {
			panTo = true
		}
		if (focus == undefined) {
			focus = true
		}
		let frame = this.getHoleFrameFeature(holeId)
		if (!frame) {
			return
		}
		this.selectFeature(frame)

		if (focus) {
			this.focus(frame)
		} else if (panTo) {
			this.fitFeature(frame, animated, rotate)
		}
	}

	focus(feature) {
		this.unfocus()
		if (!feature) {
			feature = this.selectedFeature
		}
		if (!feature) {
			return
		}
		if (feature.get('objectType') != 'frame') {
			return
		}
		this.courseFramesLayer.setOpacity(1)
		this.framesLayer.setOpacity(1)
		this.deactivateTool()
		this.#unlinkPlaylineLayer()
		feature.set('focused', true)
		let source
		if (feature.get('holeId')) {
			let holeId = feature.get('holeId')
			source = this.frameElementSources.find((s) => s.get('holeId') == holeId)
			if (!source) {
				source = new VectorSource()
				source.set('holeId', holeId)
				this.frameElementSources.push(source)
			}
			this.focusedFrameFeature = feature
			this.framesLayer.focusedHoleId = holeId
			if (this.focusedFrameLayer) {
				this.map.removeLayer(this.focusedFrameLayer)
				this.focusedFrameLayer = null
			}
			this.removeSourceListerners(this.focusedFrameSource)
			this.addSourceListerners(source)
			this.focusedFrameSource = source
			this.focusedFrameLayer = new ElementsLayer(this, source, false)
			this.focusedFrameLayer.setZIndex(5)
			this.courseFramesLayer.setOpacity(0)
			this.#linkPlaylineLayer()
		} else if (feature.get('courseId')) {
			this.framesLayer.setOpacity(0)
			let courseId = feature.get('courseId')
			source = this.courseElementSources.find((s) => s.get('courseId') == courseId)
			if (!source) {
				source = new VectorSource()
				source.set('courseId', courseId)
				this.courseElementSources.push(source)
			}
			this.focusedFrameFeature = feature
			this.courseFramesLayer.focusedCourseId = courseId
			if (this.focusedFrameLayer) {
				this.map.removeLayer(this.focusedFrameLayer)
				this.focusedFrameLayer = null
			}
			this.removeSourceListerners(this.focusedFrameSource)
			this.addSourceListerners(source)
			this.focusedFrameSource = source
			this.focusedFrameLayer = new ElementsLayer(this, source, false)
			this.focusedFrameLayer.setZIndex(5)
		}
		this.deselectFeature(feature)
		this.fitFeature(feature, false, true)
		// let geometry = feature.getGeometry()
		// let coordinates = geometry.getCoordinates()[0]
		// let angle = Math.atan2(coordinates[1][1] - coordinates[0][1], coordinates[1][0] - coordinates[0][0]) + Utils.degreesToRadians(-90)
		// this.view.setRotation(angle)
		// this.view.fit(geometry);

		this.map.addLayer(this.focusedFrameLayer)
		this.trigger('frameFocused', { feature: feature, source: source })

	}
	unfocus() {
		let f = this.focusedFrameFeature
		if (f) {
			f.unset('focused')
		} else {
			return
		}
		this.courseFramesLayer.setOpacity(1)
		this.framesLayer.setOpacity(1)
		this.deselectFeature(f)
		this.framesLayer.focusedHoleId = null
		this.courseFramesLayer.focusedCourseId = null
		this.focusedFrameSource = null
		this.focusedFrameFeature = null
		this.trigger('frameSelected', null)
		this.trigger('frameFocused', null)
		if (this.focusedFrameLayer) {
			this.map.removeLayer(this.focusedFrameLayer)
			this.focusedFrameLayer = null
		}
		// if(!this.selectedFeature || this.selectedFeature.get('holeId') == f.get('holeId')) {
		//     f.set('selected', true)
		//     this.selectedFeature = f
		// }
	}
	removeFeatureListerners(feature) {
		if (!feature) {
			return
		}
		let f = this.featureUpdated.bind(this)
		//feature.un('change', f)
		feature.un('propertychange', f)
	}
	addFeatureListerners(feature) {
		if (!feature) {
			return
		}
		let f = this.featureUpdated.bind(this)
		//feature.on('change', f)
		feature.on('propertychange', f)
	}
	removeSourceListerners(source) {
		if (!source) {
			return
		}
		let f = this.sourceUpdated.bind(this)
		source.un('addfeature', f)
		source.un('removefeature', f)
	}
	addSourceListerners(source) {
		if (!source) {
			return
		}
		let f = this.sourceUpdated.bind(this)
		source.on('addfeature', f)
		source.on('removefeature', f)
	}
	trigger(event, val1, val2, val3) {
		if (!this.listeners[event]) {
			return
		}
		for (let l of this.listeners[event]) {
			l(val1, val2, val3)
		}
	}
	featureUpdated(evt) {
		if (evt.type == 'propertychange') {

			if (ignorePropertyChangeKeys.includes(evt.key)) {
				return
			}
		}
		if (evt.target.get('objectType') === 'frame' || evt.target.get('elementName')) {
			this.trigger('featureUpdated', evt.target, evt)
		}
	}
	getFramesInExtent(extent) {
		return this.framesSource.getFeaturesInExtent(extent)
	}
	getFeaturesInExtent(extent) {
		let features = []
		for (let source of this.frameElementSources) {
			let f = source.getFeaturesInExtent(extent)
			features = features.concat(f)
		}
		let f = this.sharedElementsSource.getFeaturesInExtent(extent)
		features = features.concat(f)
		return features
	}
	sourceUpdated(evt) {

		if (evt.type == 'addfeature') {

			this.trigger('featureAdded', evt.feature, evt)
			if (evt.feature) {
				let _this = this
				let name = evt.feature.get('elementName')
				let el = getElement(name)
				if (el && el.onePerFrame === true) {
					setTimeout(() => {
						_this.deactivateTool()
					}, 20)
				}
				this.updateFeatureHighlights()
				// else if(evt.feature.get('objectType') == "frame") {
				//     evt.feature.set('selected', true)
				//     this.selectFeature(evt.feature)
				// }
			}
		} else if (evt.type == 'removefeature') {
			this.trigger('featureRemoved', evt.feature, evt)
			this.updateFeatureHighlights()
		}
		this.trigger('sourceUpdated', evt.target, evt.feature, evt)
	}
	#beginMeasurement() {
		if (this.measureTool) {
			this.map.removeInteraction(this.measureTool)
		}
		if (this.measurementVector) {
			this.map.removeLayer(this.measurementVector)
		}
		if (!this.measurementSource) {
			this.measurementSource = new VectorSource()
		}
		this.measurementVector = new VectorLayer({
			source: this.measurementSource,
			style: {
				'fill-color': 'rgba(255, 255, 255, 0.2)',
				'stroke-color': '#ffcc33',
				'stroke-width': 2,
				'circle-radius': 7,
				'circle-fill-color': '#ffcc33'
			}
		})
		const _this = this
		this.map.addLayer(this.measurementVector)
		this.measureTool = new MeasureTool(this.measurementSource, 'LineString', function (event, output, position) {
			if (event === 'end') {
				//_this.endMeasurement()
			} else {
				_this.trigger('measurementUpdated', {
					type: 'LineString',
					output: output,
					endPosition: position
				})
			}
		})
		this.map.addInteraction(this.measureTool)
	}
	#endMeasurement() {
		if (this.measureTool) {
			this.map.removeInteraction(this.measureTool)
		}
		if (this.measurementVector) {
			this.map.removeLayer(this.measurementVector)
		}
		this.measureTool = null
		this.measurementVector = null
		this.measurementSource = null
	}
	editSelectedFeature() {
		if (!this.selectedFeature) {
			return
		}
		let name = this.selectedFeature.get('elementName')
		this.activateTool(name, {
			holeId: this.selectedFeature.get('holeId'),
			courseId: this.selectedFeature.get('courseId')
		}, true)
	}
	activateTool(name, props, modifySelected) {
		if (!props) {
			props = {}
		}
		let _this = this
		let holeId = props ? props.holeId : null
		let courseId = props ? props.courseId : null
		if (this.currentTool) {
			this.deactivateTool()
		}
		let modifyFeature = null
		if (modifySelected && this.selectedFeature && this.selectedFeature.getGeometry().getType() == 'Polygon' && this.selectedFeature.get('objectType') !== 'frame') {
			modifyFeature = this.selectedFeature
		}
		this.deselectFeature()
		if (name == this.currentTool) {
			return
		}
		if (name == 'addFrame') {
			if (!this.canAddFrame(holeId)) {
				console.warn('Cannot add duplicate frame for HoleId')
				return
			}
		}
		if (name == "playline" && !this.focusedFrameFeature) {
			return
		}
		this.#setCurrentTool(name)
		if (name == 'addFrame' || name == 'addCourseFrame') {
			this.#beginAddFrame(holeId, props)
			return
		}

		if (name == 'measure') {
			this.#beginMeasurement()
			return
		}
		if (name == 'union') {
			this.unionTool = new UnionTool(function (n, r) {
				_this.sharedElementsSource.addFeature(n)
				_this.sharedElementsSource.removeFeature(r[0])
				_this.sharedElementsSource.removeFeature(r[1])
			})
			return
		}
		if (name == "playline") {
			this.#activatePlaylineTool()
			return
		}
		let element = getElement(name)
		let source = this.sharedElementsSource
		if (element && element.tooltype === 'Rectangle') {
			this.#beginAddRectangle({
				elementName: name,
			})
			return
		}
		if (element && element.frameOnly && this.focusedFrameFeature) {
			holeId = this.focusedFrameFeature.get('holeId')
			courseId = this.focusedFrameFeature.get('courseId')
			if (holeId) {
				source = this.frameElementSources.find((s) => s.get('holeId') === holeId)
				if (!source) {
					window.dispatchEvent(new CustomEvent('error', { detail: { message: `Unable to add ${element.title} . Please try again.` } }))
					return
				}
			} else if (courseId) {
				source = this.courseElementSources.find((s) => s.get('courseId') === courseId)
				if (!source) {
					window.dispatchEvent(new CustomEvent('error', { detail: { message: `Unable to add ${element.title} . Please try again.` } }))
					return
				}
			}
		} else {
			holeId = null
		}
		if (courseId) {
			props.courseId = courseId
		}
		if (holeId) {
			props.holeId = holeId
		}
		this.drawTool = new DrawElement(name, source, props)

		this.map.addInteraction(this.drawTool)
		if (modifyFeature) {
			let coordinates = modifyFeature.getGeometry().getCoordinates()[0]
			coordinates.pop()
			this.drawTool._modifyFeature = modifyFeature
			this.drawTool._modifyFeatureSource = this.getFeatureSource(modifyFeature)
			this.deleteFeature(modifyFeature)
			this.drawTool.appendCoordinates(coordinates)
		}
	}
	deactivateTool() {
		this.#setCurrentTool(null)
		if (this.addRectangleTool.attached) {
			this.#endAddRectangle()
		} else if (this.playlineLayer.isActive) {
			this.#deactivatePlaylineTool()
		} else if (this.measureTool) {
			this.#endMeasurement()
		} else if (this.drawTool) {
			this.map.removeInteraction(this.drawTool)
			this.drawTool = null
		} else {
			this.#endAddFrame()
		}
		this.trigger('drawEnd')
	}
	getHoleSource(holeId) {
		return this.frameElementSources.find((s) => s.get('holeId') == holeId)
	}
	getFeatureSource(feature) {
		for (let s of [this.sharedElementsSource, ...this.frameElementSources, ...this.courseElementSources, this.framesSource, this.courseFramesSource]) {
			if (s.getFeatures().includes(feature)) {
				return s
			}
		}
		// let holeId = feature.get('holeId')
		// let courseId = feature.get('courseId')
		// if (feature.get('objectType') == 'frame') {
		// 	if (courseId !== undefined) {
		// 		return this.courseFramesSource
		// 	} else if (holeId === feature.get('holeId')) {
		// 		return this.framesSource
		// 	}
		// } else if (holeId) {
		// 	return this.frameElementSources.find((s) => s.get('holeId') == holeId)
		// } else if (courseId) {
		// 	return this.courseElementSources.find((s) => s.get('courseId') == courseId)
		// }
		// return this.sharedElementsSource
		return null
	}
	currentSource() {
		if (this.focusedFrameSource) {
			return this.focusedFrameSource
		}
		return this.sharedElementsSource
	}
	sourceHasElement(source, element) {
		if (!source) {
			source = this.currentSource()
		}
		let features = source.getFeatures()
		for (let f of features) {
			if (f.get('elementName') === element.name) {
				return true
			}
		}
		return false
	}
	canDraw(name, holeId) {
		if (name == 'addFrame') {
			if (!holeId || !this.canAddFrame(holeId)) {
				return false
			}
			return true
		}
		let element = getElement(name)
		if (!this.focusedFrameSource) {
			return element.frameOnly === false
		}
		if (!element.onePerFrame) {
			return true
		}
		let source = this.currentSource()
		let features = source.getFeatures()
		for (let f of features) {
			if (f.get('elementName') === element.name) {
				return false
			}
		}
		return true
	}
	canAddFrame(holeId) {
		if (!holeId) {
			return true
		}
		let arr = this.framesSource.getFeatures()
		for (let a of arr) {
			let h = a.get('holeId')
			if (h === holeId) {
				return false
			}
		}
		return true
	}
	getPlaylineProperties(holeId) {
		if (!holeId) {
			console.warn('no holeid')
			return null
		}
		let playline = this.playlines[holeId] ?? {
			coordinates: [],
			teePoint: null,
			greenPoint: "greencenter"
		}
		let source = this.frameElementSources.find((s) => s.get('holeId') == holeId)
		if (!source) {
			console.warn('no source for hole')
			return null
		}
		let props = {
			holeId: holeId,
			teePoint: playline.teePoint,
			greenPoint: playline.greenPoint,
			coordinates: playline.coordinates,
			frameSource: source
		}
		let teePoint = source.getFeatures().find((f) => f.get('elementName') === playline.teePoint)
		let greenPoint = source.getFeatures().find((f) => f.get('elementName') === playline.greenPoint)
		if (!teePoint || !greenPoint) {
			console.warn('tee or greenpoint missing')
			return null
		}
		props.assembledCoordinates = [teePoint.getGeometry().getCoordinates(), ...playline.coordinates, greenPoint.getGeometry().getCoordinates()]
		props.greenPointCoordiantes = greenPoint.getGeometry().getCoordinates()
		props.teePointCoordinates = teePoint.getGeometry().getCoordinates()
		return props
	}
	#setCurrentTool(name) {
		this.currentTool = name
		let element = getElement(name)
		if (this.currentTool) {
			document.getElementById(this.target).style.cursor = `crosshair`
		} else {
			document.getElementById(this.target).style.cursor = 'default'
		}
		this.trigger('currentToolChange', name, element)
	}
	#beginAddRectangle(properties) {
		this.addRectangleTool.attach(this.map, this.sharedElementsSource, properties)
	}
	#endAddRectangle() {
		this.addRectangleTool.detach()
	}
	#beginAddFrame(holeId, props) {
		if (holeId) {
			this.addFrameTool.setFixedRatio(import.meta.env.VITE_RENDER_HEIGHT / import.meta.env.VITE_RENDER_WIDTH)
			let props = deconstructHoleId(holeId)
			this.map.addLayer(this.addFramesLayer)
			this.addFrameTool.attach(this.map, this.addFramesSource, {
				holeId: holeId,
				displayName: props.groupShortName + props.holeNumber,
				objectType: 'frame',
				frameId: nanoid(50),
				unfinished: true
			})
		} else {
			this.addFrameTool.setFixedRatio(null)
			this.map.addLayer(this.addFramesLayer)
			this.addFrameTool.attach(this.map, this.addFramesSource, {
				courseId: props?.courseId ?? nanoid(),
				displayName: props?.displayName ?? "Overview outline",
				objectType: 'frame',
				frameId: nanoid(50),
				unfinished: true
			})
		}
	}
	#endAddFrame() {
		let features = this.addFramesSource.getFeatures()
		if (features.length > 0) {
			if (features[0].get('holeId')) {
				this.framesSource.addFeature(features[0])
			} else {
				this.courseFramesSource.addFeature(features[0])
			}

			this.addFramesSource.removeFeature(features[0])
		}
		this.map.removeLayer(this.addFramesLayer)
		this.addFrameTool.detach()
	}
	#unlinkPlaylineLayer() {
		this.playlineLayer.unlink()
	}
	#linkPlaylineLayer() {
		if (!this.focusedFrameSource) {
			console.warn('No focused frame source')
			return
		}
		let holeId = this.focusedFrameFeature.get('holeId')
		if (!holeId) {
			console.warn('no holeid')
			return
		}
		let playlineProps = this.getPlaylineProperties(holeId)

		this.playlineLayer.link(playlineProps)
	}
	#activatePlaylineTool() {
		this.playlineLayer.activate()
	}
	#deactivatePlaylineTool() {
		this.playlineLayer.deactivate()
	}
}
